import type {
  DailyItem,
  DisciplioSettings,
  GoalMetric,
  CalendarHabitTimeScopeMode,
  HabitTimeScope,
  MonthlyGoal,
  UserProfile,
  WeeklyGoal,
} from "../disciplio/types";
import { getTodayKey, shiftDateKey } from "../disciplio/logic";
import { DisciplioApiError, hasApiSession, request } from "./core";
import { looksLikeUuid } from "./shared";
import type {
  DailyCompletionEntry,
  DailyPlanItemResponse,
  DailyPlanResponse,
  DailyPlanSummaryEntry,
  DailyReflectionResponse,
  StreakSummary,
  GoalRequest,
  GoalResponse,
  MonthlyPlanResponse,
  MonthlyPlanItemResponse,
  WeeklyPlanResponse,
} from "./types";
import {
  toDailyItem,
  toDailyItemRequest,
  toDailyItemUpdateRequest,
  toMonthlyGoal,
  toMonthlyPlanItemRequest,
  toWeeklyGoal,
  toWeeklyPlanItemRequest,
} from "./adapters";

export class MissingMonthlyPlanYearGoalError extends Error {
  constructor() {
    super("The selected year goal is no longer available");
    this.name = "MissingMonthlyPlanYearGoalError";
  }
}

export async function saveDailyItem(
  date: string,
  item: DailyItem
): Promise<DailyPlanItemResponse | null> {
  if (!hasApiSession()) return null;
  return request<DailyPlanItemResponse>(`/api/daily-plans/${date}/items`, {
    method: "POST",
    auth: true,
    body: JSON.stringify(toDailyItemRequest(item)),
  });
}

export async function getDailyPlan(date: string): Promise<DailyItem[]> {
  if (!hasApiSession()) return [];
  const plan = await request<DailyPlanResponse>(`/api/daily-plans/${date}`, {
    method: "GET",
    auth: true,
  });
  return plan.items.map(toDailyItem);
}

export async function getCompletionHistory(
  days = 730,
  settings?: DisciplioSettings
): Promise<Record<string, boolean>> {
  if (!hasApiSession()) return {};
  const end = getTodayKey(settings);
  const start = shiftDateKey(end, -Math.max(days - 1, 0));
  const params = new URLSearchParams({
    from: start,
    to: end,
  });
  const entries = await request<DailyCompletionEntry[]>(
    `/api/daily-plans/completion-history?${params.toString()}`,
    {
      method: "GET",
      auth: true,
    }
  );
  return entries.reduce<Record<string, boolean>>((history, entry) => {
    history[entry.date] = entry.completed;
    return history;
  }, {});
}

export async function getDailyPlanSummary(
  from: string,
  to: string
): Promise<DailyPlanSummaryEntry[]> {
  if (!hasApiSession()) return [];
  const params = new URLSearchParams({ from, to });
  return request<DailyPlanSummaryEntry[]>(
    `/api/daily-plans/summary?${params.toString()}`,
    {
      method: "GET",
      auth: true,
    }
  );
}

export async function getStreakSummary(
  asOf: string,
  month = asOf.slice(0, 7)
): Promise<StreakSummary | null> {
  if (!hasApiSession()) return null;
  const params = new URLSearchParams({ asOf, month });
  return request<StreakSummary>(`/api/streaks/summary?${params.toString()}`, {
    method: "GET",
    auth: true,
  });
}

export async function updateDailyItem(
  date: string,
  item: DailyItem,
  habitTimeScope?: HabitTimeScope,
  omitDone = false,
  calendarHabitTimeScopeMode?: CalendarHabitTimeScopeMode
): Promise<DailyPlanItemResponse | null> {
  if (!hasApiSession() || !looksLikeUuid(item.id)) return null;
  const { done, ...updateRequest } = toDailyItemUpdateRequest(item);
  return request<DailyPlanItemResponse>(
    `/api/daily-plans/${date}/items/${item.id}`,
    {
      method: "PUT",
      auth: true,
      body: JSON.stringify({
        ...updateRequest,
        ...(!omitDone ? { done } : {}),
        ...(habitTimeScope ? { habitTimeScope } : {}),
        ...(calendarHabitTimeScopeMode === "remember"
          ? { rememberCalendarHabitTimeScope: true }
          : calendarHabitTimeScopeMode === "reuse"
            ? { useRememberedCalendarHabitTimeScope: true }
            : {}),
      }),
    }
  );
}

export async function deleteDailyItem(
  date: string,
  id: string,
  habitScope?: "ONE" | "ALL"
) {
  if (!hasApiSession() || !looksLikeUuid(id)) return;
  const suffix = habitScope ? `?habitScope=${habitScope}` : "";
  await request(`/api/daily-plans/${date}/items/${id}${suffix}`, {
    method: "DELETE",
    auth: true,
  });
}

export async function moveDailyItem(
  fromDate: string,
  id: string,
  toDate: string,
  timeRange?: string
) {
  if (!hasApiSession() || !looksLikeUuid(id)) return null;
  return request<DailyPlanItemResponse>(
    `/api/daily-plans/${fromDate}/items/${id}/move`,
    {
      method: "POST",
      auth: true,
      body: JSON.stringify({
        toDate,
        timeRange: timeRange ?? null,
      }),
    }
  );
}

export async function duplicateDailyItem(
  sourceDate: string,
  itemId: string,
  targetDates: string[],
  keepTime: boolean,
  keepWeeklyGoalConnected: boolean
): Promise<DailyPlanItemResponse[] | null> {
  if (!hasApiSession() || !looksLikeUuid(itemId)) return null;
  return request<DailyPlanItemResponse[]>(
    `/api/daily-plans/${sourceDate}/items/${itemId}/duplicate`,
    {
      method: "POST",
      auth: true,
      body: JSON.stringify({ targetDates, keepTime, keepWeeklyGoalConnected }),
    }
  );
}

export async function completeDailyItem(itemId: string, done: boolean) {
  if (!hasApiSession() || !looksLikeUuid(itemId)) return;
  await request(`/api/daily-plan-items/${itemId}/complete`, {
    method: "PATCH",
    auth: true,
    body: JSON.stringify({ done }),
  });
}

export async function completeMonthlyGoal(
  itemId: string,
  done: boolean
): Promise<MonthlyPlanItemResponse | null> {
  if (!hasApiSession() || !looksLikeUuid(itemId)) return null;
  return request<MonthlyPlanItemResponse>(
    `/api/monthly-plans/items/${itemId}/complete`,
    {
      method: "PATCH",
      auth: true,
      body: JSON.stringify({ done }),
    }
  );
}

export async function completeHabitOccurrence(
  date: string,
  itemId: string,
  done: boolean
) {
  if (!hasApiSession() || !looksLikeUuid(itemId)) return;
  await request(`/api/daily-plans/${date}/habit-items/${itemId}/complete`, {
    method: "PATCH",
    auth: true,
    body: JSON.stringify({ done }),
  });
}

export async function saveReflection(
  date: string,
  content: string,
  tomorrowContext?: string | null
): Promise<boolean> {
  if (!hasApiSession() || !content.trim()) return false;
  await request(`/api/daily-reflections/${date}`, {
    method: "POST",
    auth: true,
    body: JSON.stringify({ content, tomorrowContext: tomorrowContext ?? null }),
  });
  return true;
}

export async function getDailyReflection(
  date: string
): Promise<DailyReflectionResponse | null> {
  if (!hasApiSession()) return null;
  try {
    return await request<DailyReflectionResponse>(
      `/api/daily-reflections/${date}`,
      {
        method: "GET",
        auth: true,
      }
    );
  } catch (error) {
    if (error instanceof DisciplioApiError && error.status === 404) return null;
    return null;
  }
}

export async function listMonthlyPlans(): Promise<MonthlyPlanResponse[]> {
  if (!hasApiSession()) return [];
  return request<MonthlyPlanResponse[]>("/api/monthly-plans", {
    method: "GET",
    auth: true,
  });
}

export async function getMonthlyPlan(
  year: number,
  month: number
): Promise<MonthlyPlanResponse | null> {
  if (!hasApiSession()) return null;
  try {
    return await request<MonthlyPlanResponse>(
      `/api/monthly-plans/by-month/${year}/${month}`,
      { method: "GET", auth: true }
    );
  } catch (error) {
    if (error instanceof DisciplioApiError && error.status === 404) return null;
    return null;
  }
}

export async function listGoals(): Promise<GoalResponse[]> {
  if (!hasApiSession()) return [];
  return request<GoalResponse[]>("/api/goals", {
    method: "GET",
    auth: true,
  });
}

export async function syncProfileGoals(
  profile: UserProfile
): Promise<GoalResponse[]> {
  if (!hasApiSession()) return [];
  const existing = (await listGoals().catch(() => [])).filter(
    (goal) => goal.status !== "DELETED"
  );
  const globals = existing
    .filter((goal) => goal.scope === "GLOBAL")
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const years = existing
    .filter((goal) => goal.scope === "YEAR")
    .sort((a, b) => a.sortOrder - b.sortOrder);
  // Match local entries to backend goals by stable goalId (profile.globalGoalIds/
  // yearGoalIds are index-aligned with profile.globalGoals/yearGoals, see
  // UserProfile in disciplio/types.ts) rather than by array position. Position alone
  // is unsafe: a local delete/insert can leave profile.globalGoals reordered relative
  // to `globals` (which is re-sorted from the server on every call), and matching by
  // index in that case would silently overwrite the wrong backend goal. An entry with
  // no known id (new locally, e.g. freshly added or copied) falls back to creating a
  // new backend goal instead of updating an unrelated one.
  const globalById = new Map(globals.map((goal) => [goal.goalId, goal]));
  const yearById = new Map(years.map((goal) => [goal.goalId, goal]));
  const globalByTitle = new Map<string, GoalResponse>();
  const yearByTitle = new Map(
    years.map((goal) => [goal.title.trim().toLowerCase(), goal])
  );

  const matchedGlobalIds = new Set<string>();
  for (const [index, titleRaw] of profile.globalGoals.entries()) {
    const title = titleRaw.trim();
    if (!title) continue;
    const key = title.toLowerCase();
    const knownId = profile.globalGoalIds[index];
    const current = knownId ? globalById.get(knownId) : undefined;
    const nextCompleted = Boolean(profile.globalGoalCompleted[title]);
    const metric = profile.globalGoalMetrics[title];
    const payload = goalPayload("GLOBAL", title, index, nextCompleted, metric);
    const saved = current
      ? await updateGoal(current.goalId, payload).catch(() => null)
      : await createGoal(payload).catch(() => null);
    if (saved) {
      globalByTitle.set(key, saved);
      matchedGlobalIds.add(saved.goalId);
    }
  }
  for (const extra of globals.filter(
    (goal) => !matchedGlobalIds.has(goal.goalId)
  ))
    await deleteGoal(extra.goalId).catch(() => undefined);

  const matchedYearIds = new Set<string>();
  for (const [index, titleRaw] of profile.yearGoals.entries()) {
    const title = titleRaw.trim();
    if (!title) continue;
    const key = title.toLowerCase();
    const parentTitle = profile.yearGoalLinks[title]?.trim().toLowerCase();
    const parent = parentTitle ? globalByTitle.get(parentTitle) : undefined;
    const knownId = profile.yearGoalIds[index];
    const existingYear =
      (knownId ? yearById.get(knownId) : undefined) ??
      years.find((goal) => goal.title.trim().toLowerCase() === key);
    const metric = profile.yearGoalMetrics[title];
    const targetCompleted = Boolean(profile.yearGoalCompleted[title]);
    if (!existingYear) {
      const created = await createGoal({
        ...goalPayload("YEAR", title, index, targetCompleted, metric),
        parentGoalId: parent?.goalId ?? null,
      }).catch(() => null);
      if (created) {
        yearByTitle.set(key, created);
        matchedYearIds.add(created.goalId);
      }
      continue;
    }
    matchedYearIds.add(existingYear.goalId);
    const parentChanged =
      (existingYear.parentGoalId ?? null) !== (parent?.goalId ?? null);
    const completedChanged =
      (existingYear.status === "ARCHIVED") !== targetCompleted;
    const sortChanged = existingYear.sortOrder !== index;
    const metricChanged =
      existingYear.targetValue !== (metric?.target ?? 0) ||
      existingYear.targetUnit !== (metric?.unit ?? "") ||
      existingYear.progressValue !== (metric?.progress ?? 0);
    if (parentChanged || completedChanged || sortChanged || metricChanged) {
      const updated = await updateGoal(existingYear.goalId, {
        ...goalPayload("YEAR", title, index, targetCompleted, metric),
        parentGoalId: parent?.goalId ?? null,
      }).catch(() => null);
      if (updated) yearByTitle.set(key, updated);
    }
  }
  for (const extra of years.filter((goal) => !matchedYearIds.has(goal.goalId)))
    await deleteGoal(extra.goalId).catch(() => undefined);
  return listGoals().catch(() => existing);
}

function goalPayload(
  scope: GoalRequest["scope"],
  title: string,
  sortOrder: number,
  completed: boolean,
  metric?: GoalMetric
): GoalRequest {
  return {
    scope,
    title,
    sortOrder,
    completed,
    targetValue: metric?.target ?? 0,
    targetUnit: metric?.unit ?? "",
    progressValue: metric?.progress ?? 0,
  };
}

async function deleteGoal(goalId: string): Promise<void> {
  await request<void>(`/api/goals/${goalId}`, { method: "DELETE", auth: true });
}

export async function saveMonthlyPlan(
  year: number,
  month: number,
  goals: MonthlyGoal[],
  version?: number | null
): Promise<MonthlyGoal[]> {
  if (!hasApiSession()) {
    throw new DisciplioApiError(401, "Authentication required");
  }
  const goalsIndex = await listGoals();
  const yearGoals = goalsIndex.filter(
    (goal) => goal.scope === "YEAR" && goal.status !== "DELETED"
  );
  const yearGoalById = new Map(yearGoals.map((goal) => [goal.goalId, goal]));
  const yearGoalByTitle = new Map(
    yearGoals.map((goal) => [goal.title.trim().toLowerCase(), goal])
  );
  const resolvedGoals = goals.map((goal) => {
    const selectedYearGoalId =
      goal.yearGoalId && looksLikeUuid(goal.yearGoalId)
        ? goal.yearGoalId
        : null;
    const selectedYearGoalTitle = goal.yearGoal?.trim();
    if (!selectedYearGoalId && !selectedYearGoalTitle) return goal;

    const yearGoal = selectedYearGoalId
      ? yearGoalById.get(selectedYearGoalId)
      : yearGoalByTitle.get(selectedYearGoalTitle!.toLowerCase());
    if (!yearGoal) throw new MissingMonthlyPlanYearGoalError();
    return { ...goal, yearGoalId: yearGoal.goalId, yearGoal: yearGoal.title };
  });
  const plan = await request<MonthlyPlanResponse>("/api/monthly-plans", {
    method: "POST",
    auth: true,
    body: JSON.stringify({
      year,
      month,
      status: "ACTIVE",
      ...(version == null ? {} : { version }),
      items: resolvedGoals.map(toMonthlyPlanItemRequest),
    }),
  });

  const yearGoalTitleById = new Map(
    goalsIndex
      .filter((goal) => goal.scope === "YEAR" && goal.status !== "DELETED")
      .map((goal) => [goal.goalId, goal.title])
  );

  return plan.items.map((item, index) => ({
    ...toMonthlyGoal(item),
    yearGoal:
      resolvedGoals[index]?.yearGoal ??
      (item.yearGoalId ? (yearGoalTitleById.get(item.yearGoalId) ?? "") : ""),
    yearGoalId:
      resolvedGoals[index]?.yearGoalId ?? item.yearGoalId ?? undefined,
    completed: resolvedGoals[index]?.completed ?? item.status === "ARCHIVED",
  }));
}

export async function listWeeklyPlans(): Promise<WeeklyPlanResponse[]> {
  if (!hasApiSession()) return [];
  return request<WeeklyPlanResponse[]>("/api/weekly-plans", {
    method: "GET",
    auth: true,
  });
}

export async function getWeeklyPlan(
  weekStartDate: string
): Promise<WeeklyPlanResponse | null> {
  if (!hasApiSession()) return null;
  try {
    return await request<WeeklyPlanResponse>(
      `/api/weekly-plans/by-week/${weekStartDate}`,
      { method: "GET", auth: true }
    );
  } catch (error) {
    if (error instanceof DisciplioApiError && error.status === 404) return null;
    return null;
  }
}

export async function saveWeeklyPlan(
  weekStartDate: string,
  goals: WeeklyGoal[],
  version?: number | null
): Promise<WeeklyGoal[]> {
  if (!hasApiSession()) return goals;
  const plan = await request<WeeklyPlanResponse>("/api/weekly-plans", {
    method: "POST",
    auth: true,
    body: JSON.stringify({
      weekStartDate,
      status: "ACTIVE",
      ...(version == null ? {} : { version }),
      items: goals.map(toWeeklyPlanItemRequest),
    }),
  });
  return plan.items.map((item, index) => ({
    ...toWeeklyGoal(item, plan.weekStartDate),
    completed: goals[index]?.completed ?? item.status === "ARCHIVED",
  }));
}

async function createGoal(payload: GoalRequest): Promise<GoalResponse> {
  return request<GoalResponse>("/api/goals", {
    method: "POST",
    auth: true,
    body: JSON.stringify(payload),
  });
}

async function updateGoal(
  goalId: string,
  payload: GoalRequest
): Promise<GoalResponse> {
  return request<GoalResponse>(`/api/goals/${goalId}`, {
    method: "PUT",
    auth: true,
    body: JSON.stringify(payload),
  });
}
